import * as React from 'react';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { IDepotGeo, ICapacite } from '../../../models';
import styles from './DepotMap.module.scss';

export interface IDepotMapProps {
  depots: IDepotGeo[];
  capacites: ICapacite[];
}

const FRANCE_CENTER: L.LatLngExpression = [46.8, 2.5];
const FRANCE_ZOOM = 6;

export const DepotMap: React.FC<IDepotMapProps> = ({ depots, capacites }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<L.Map | null>(null);

  // Volume per depot for sizing
  const depotVolumes = React.useMemo(() => {
    const map: Record<number, number> = {};
    capacites.forEach(c => {
      map[c.DepotId] = (map[c.DepotId] || 0) + c.VolumeAlloue;
    });
    return map;
  }, [capacites]);

  const activeDepotIds = React.useMemo(
    () => new Set(capacites.map(c => c.DepotId)),
    [capacites]
  );

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: false,
    }).setView(FRANCE_CENTER, FRANCE_ZOOM);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update markers when data changes
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear existing markers
    map.eachLayer(layer => {
      if (layer instanceof L.CircleMarker) {
        map.removeLayer(layer);
      }
    });

    const maxVol = Math.max(...Object.values(depotVolumes), 1);

    depots.forEach(depot => {
      const isActive = activeDepotIds.has(depot.Id);
      const vol = depotVolumes[depot.Id] || 0;
      const radius = isActive ? Math.max(8, (vol / maxVol) * 28) : 5;

      const marker = L.circleMarker([depot.Latitude, depot.Longitude], {
        radius,
        fillColor: isActive ? '#2B5EA7' : '#BDBDBD',
        color: isActive ? '#1B3D6F' : '#999',
        weight: 2,
        opacity: 1,
        fillOpacity: isActive ? 0.7 : 0.3,
      }).addTo(map);

      const popupContent = isActive
        ? `<div style="font-family:'DM Sans',sans-serif;font-size:13px">
            <strong style="color:#1B3D6F">${depot.Nom}</strong><br/>
            <span style="color:#6A737D">${depot.Region}</span><br/>
            <span style="font-size:18px;font-weight:700;color:#2B5EA7">${vol.toLocaleString('fr-FR')}</span>
            <span style="color:#6A737D"> m³ alloués</span>
          </div>`
        : `<div style="font-family:'DM Sans',sans-serif;font-size:13px">
            <strong style="color:#999">${depot.Nom}</strong><br/>
            <span style="color:#BDBDBD">Aucune allocation</span>
          </div>`;

      marker.bindPopup(popupContent);
    });
  }, [depots, depotVolumes, activeDepotIds]);

  return (
    <div className={styles.mapWrapper}>
      <div className={styles.mapTitle}>Implantations</div>
      <div ref={containerRef} className={styles.mapContainer} />
    </div>
  );
};
